import { useState, useCallback } from "react";
import { dashboardBackendUrl } from "../../config/gateway";

interface DetailLogRaw {
  id: number;
  codLog?: number;
  process?: string;
  message?: string;
  request?: string;
  name?: string;
  refid?: string;
  refId?: string;
  response?: number;
  status?: string;
  execution_status?: string;
  executionStatus?: string;
  item_status?: string;
  itemStatus?: string;
}

export interface DetailLog {
  id: number;
  codLog: number;
  process: string;
  message: string;
  request: string;
  name: string;
  refid: string;
  response: number;
  status: string;
  itemStatus: string;
}

export interface LogFilterParams {
  fromDate?: string;
  toDate?: string;
  status?: string;
  itemStatus?: string;
  refId?: string;
  /** Doc §2.2 default is false */
  onlyFailed?: boolean;
}

const mapDetailLogItem = (item: DetailLogRaw): DetailLog => ({
  id: item.id,
  codLog: item.codLog ?? 0,
  process: item.process ?? "",
  message: item.message ?? "",
  request: item.request ?? "",
  name: item.name ?? "",
  refid: item.refid ?? item.refId ?? "",
  response: item.response ?? 0,
  status: item.execution_status ?? item.executionStatus ?? item.status ?? "",
  itemStatus: item.item_status ?? item.itemStatus ?? "",
});

interface UseGetDetailLogsReturn {
  detailLog: DetailLog[] | null;
  loadingDetailLog: boolean;
  errorDetailLog: string | null;
  totalPages: number;
  currentPage: number;
  /** Doc response field `cant` (total matching items) */
  totalElements: number;
  fetchDetailLog: (
    logId: string,
    selectedProcessType: number,
    page: number,
    size: number,
    filters?: LogFilterParams
  ) => Promise<void>;
}

const useGetDetailLogs = (): UseGetDetailLogsReturn => {
  const [detailLog, setDetailLog] = useState<DetailLog[] | null>(null);
  const [loadingDetailLog, setLoadingDetailLog] = useState<boolean>(false);
  const [errorDetailLog, setErrorDetailLog] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalElements, setTotalElements] = useState<number>(0);

  const fetchDetailLog = useCallback(
    async (
      logId: string,
      selectedProcessType: number,
      page: number,
      size: number,
      filters: LogFilterParams = {}
    ): Promise<void> => {
      setLoadingDetailLog(true);
      setErrorDetailLog(null);

      try {
        const tokenStorage = localStorage.getItem("token");
        const token = tokenStorage ? tokenStorage.replace(/['"]+/g, "") : null;
        if (!token) {
          throw new Error(
            "Token de acceso no encontrado. Inicia sesión primero."
          );
        }

        // ASSUMPTION: §2.2 documents `refId`; some backends bind the entity field `refid`.
        const onlyFailed = filters.onlyFailed === true;
        const refId = (filters.refId ?? "").trim();

        const params = new URLSearchParams({
          processType: String(selectedProcessType),
          logId,
          fromDate: filters.fromDate ?? "",
          toDate: filters.toDate ?? "",
          refId,
          refid: refId,
          status: filters.status ?? "",
          itemStatus: filters.itemStatus ?? "",
          onlyFailed: String(onlyFailed),
          page: String(page),
          size: String(size),
        });

        const response = await fetch(
          `${dashboardBackendUrl.logFilter()}?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            `Error al obtener LOGS: ${response.status} - ${response.statusText}`
          );
        }

        if (!Array.isArray(data?.logItems)) {
          throw new Error(
            "Formato de respuesta inesperado. Se esperaba un array de los Logs."
          );
        }

        const cant =
          typeof data.cant === "number"
            ? data.cant
            : typeof data.totalElements === "number"
              ? data.totalElements
              : data.logItems.length;

        setDetailLog(data.logItems.map(mapDetailLogItem));
        setTotalPages(
          typeof data.totalPages === "number"
            ? data.totalPages
            : Math.ceil(cant / size) || 0
        );
        setCurrentPage(
          typeof data.currentPage === "number" ? data.currentPage : page
        );
        setTotalElements(cant);
      } catch (err) {
        console.error("Error fetching log detail:", err);
        setDetailLog(null);
        setTotalElements(0);
        setTotalPages(0);
        setErrorDetailLog(
          err instanceof Error
            ? err.message
            : "Error desconocido al realizar la solicitud"
        );
      } finally {
        setLoadingDetailLog(false);
      }
    },
    []
  );

  return {
    detailLog,
    loadingDetailLog,
    errorDetailLog,
    totalPages,
    currentPage,
    totalElements,
    fetchDetailLog,
  };
};

export default useGetDetailLogs;
